import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
 
 

import {
  Button,
  Tabs,
  Spinner,
  EmptyState,
} from 'src/DesignSystem';
import {
  surface,
  canvas,
  textPrimary,
  textSecondary,
  textTertiary,
  borderSubtle,
  successText,
  dangerText,
  warningText,
  warningLight,
} from 'src/DesignSystem/tokens/colors';
import { spSm, spMd, spLg, sp2xl } from 'src/DesignSystem/tokens/spacing';
import { radiusLg, radiusMd } from 'src/DesignSystem/tokens/radii';

import { useModal } from 'src/hooks/Modal';
import api from 'src/services/api';
 

import {
  ReportRow,
  useOpenWithdrawAnalysisModal,
} from './WithdrawAnalysisModal';
import handleFinanceReportPDF from './CheckFinance/handleFinanceReportPDF';
import { FiAlertTriangle, FiFileText } from 'react-icons/fi';
import { FaChartArea, FaLightbulb } from 'react-icons/fa';

/* ------------------------------------------------------------------ */
/* Tipos                                                               */
/* ------------------------------------------------------------------ */

interface ReportItem {
  code: string;
  event: string;
  saque: number;
  date: string;          // já formatado (dayDate do backend)
  last: string;          // ISO cru (funding_date || date_created)
  url: string;           // events: "project-url"; invoices: "project-url-invoice"
  payment_id?: string;   // backend passa a devolver
  id?: string;           // id do saque (Pagar.me). Se ausente, não teve saque.
}

interface SaldoState {
  status: 'loading' | 'done' | 'error';
  value: number | null;   // saldo em REAIS (já convertido de centavos)
  rows?: ReportRow[];     // cache do checkSaldoStatic (em centavos)
}

type SaldoMap = Record<string, SaldoState>;
type BoolMap = Record<string, boolean>;
type TabKey = 'eventos' | 'mensalidades';

/* ------------------------------------------------------------------ */
/* Utils                                                               */
/* ------------------------------------------------------------------ */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const THREE_WEEKS_DAYS = 21;

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(v || 0);

const stripInvoiceSuffix = (url: string) => url.replace(/-invoice$/, '');

const filterByLast3Weeks = (items: ReportItem[]) => {
  const threshold = Date.now() - THREE_WEEKS_DAYS * MS_PER_DAY;
  return items.filter(i => {
    if (!i?.last) return false;
    const t = new Date(i.last).getTime();
    return !Number.isNaN(t) && t >= threshold;
  });
};

const sortByLastDesc = <T extends { last?: string }>(items: T[]) =>
  [...items].sort((a, b) => {
    const aT = a?.last ? new Date(a.last).getTime() : 0;
    const bT = b?.last ? new Date(b.last).getTime() : 0;
    return bT - aT;
  });

const getLastSaqueSaldoCents = (rows: ReportRow[]): number => {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.operation_type === 'Saque') {
      const s = rows[i]?.saldo;
      return typeof s === 'number' ? s : 0;
    }
  }
  return 0;
};

const buildReportPath = (url: string, isInvoice: boolean) => {
  const projectId = isInvoice ? stripInvoiceSuffix(url) : url;
  return isInvoice
    ? `/check-saldo-static/${projectId}/faturas/SaleInvoice/json`
    : `/check-saldo-static/${projectId}/json`;
};

/* Detecta se o saque da Pagar.me (item.id) NÃO está presente nas rows do
   checkSaldoStatic. Provavelmente foi gravado em outro project_id. */
const isOrphanSaque = (item: ReportItem, entry?: SaldoState): boolean => {
  if (!item.id) return false;
  if (!entry || entry.status !== 'done' || !entry.rows) return false;
  return !entry.rows.some(r => r.movement_id?.toString() === item.id?.toString());
};

/* ------------------------------------------------------------------ */
/* Styled                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spLg}px;
`;

const TableWrapper = styled.div`
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusLg};
  overflow: hidden;
  background: ${surface};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: ${textPrimary};
`;

const Thead = styled.thead`
  background: ${canvas};
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  color: ${textSecondary};
  padding: ${spMd}px ${spLg}px;
  border-bottom: 1px solid ${borderSubtle};
  letter-spacing: 0.4px;
`;

const Td = styled.td`
  padding: ${spMd}px ${spLg}px;
  border-bottom: 1px solid ${borderSubtle};
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:last-child ${Td} {
    border-bottom: none;
  }
  &:hover {
    background: ${canvas};
  }
`;

const SaldoText = styled.span<{ $kind: 'positive' | 'negative' | 'neutral' }>`
  font-weight: 600;
  color: ${p =>
    p.$kind === 'positive'
      ? successText
      : p.$kind === 'negative'
      ? dangerText
      : textSecondary};
`;

const OrphanBadge = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${warningLight};
  color: ${warningText};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusMd};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;

const Modules = styled.div`
  display: flex;
  gap: ${spSm}px;
  flex-wrap: wrap;
`;

const CenterBox = styled.div`
  display: flex;
  justify-content: center;
  padding: ${sp2xl}px;
`;

/* ------------------------------------------------------------------ */
/* Conteúdo do modal                                                   */
/* ------------------------------------------------------------------ */

interface WithdrawReportContentProps {
  modalKey: string;
}

const WithdrawReportContent: React.FC<WithdrawReportContentProps> = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ReportItem[]>([]);
  const [invoices, setInvoices] = useState<ReportItem[]>([]);
  const [saldos, setSaldos] = useState<SaldoMap>({});
  const [pdfGenerating, setPdfGenerating] = useState<BoolMap>({});
  const [suggestionsLoading, setSuggestionsLoading] = useState<BoolMap>({});
  const [tab, setTab] = useState<TabKey>('eventos');

 
  const openAnalysis = useOpenWithdrawAnalysisModal();

  useEffect(() => {
    let cancelled = false;

    const fetchSaldo = async (item: ReportItem, isInvoice: boolean) => {
      if (cancelled) return;

      setSaldos(prev => ({
        ...prev,
        [item.url]: { status: 'loading', value: null },
      }));

      try {
        const { data } = await api.get(buildReportPath(item.url, isInvoice));
        if (cancelled) return;

        const rows: ReportRow[] = data?.rows || [];
        const saldoCents = getLastSaqueSaldoCents(rows);

        setSaldos(prev => ({
          ...prev,
          [item.url]: {
            status: 'done',
            value: saldoCents / 100,
            rows,
          },
        }));
      } catch (err) {
        if (cancelled) return;
        setSaldos(prev => ({
          ...prev,
          [item.url]: { status: 'error', value: null },
        }));
      }
    };

    const runQueue = async (
      queueItems: Array<{ item: ReportItem; isInvoice: boolean }>,
      concurrency = 2,
    ) => {
      const queue = [...queueItems];
      const worker = async () => {
        while (!cancelled && queue.length > 0) {
          const next = queue.shift();
          if (!next) break;
          await fetchSaldo(next.item, next.isInvoice);
        }
      };
      await Promise.all(
        Array.from({ length: concurrency }, () => worker()),
      );
    };

    const load = async () => {
      try {
        const { data } = await api.get('/check-full-finance-report-json');
        if (cancelled) return;

        const filteredEvents = data?.events || []  
        const filteredInvoices = data?.invoices || []  

        setEvents(filteredEvents);
        setInvoices(filteredInvoices);
        setLoading(false);

        const queue = sortByLastDesc(
          [
            ...filteredEvents.map(i => ({ item: i, isInvoice: false })),
            ...filteredInvoices.map(i => ({ item: i, isInvoice: true })),
          ].map(entry => ({ ...entry, last: entry.item.last })),
        ).map(({ item, isInvoice }) => ({ item, isInvoice }));

        runQueue(queue, 2);
      } catch (err) {
        console.error('Erro ao carregar relatório de saques', err);
        if (!cancelled) {
          setError('Não foi possível carregar o relatório.');
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  /* --------------------------------------------------------------- */
  /* Helpers                                                          */
  /* --------------------------------------------------------------- */

  const ensureRows = async (
    item: ReportItem,
    isInvoice: boolean,
  ): Promise<ReportRow[]> => {
    const cachedEntry = saldos[item.url];
    // Usa cache quando disponível, mesmo que rows seja vazio — vazio é uma
    // resposta válida (caso órfão: saque registrado em outro project_id).
    if (cachedEntry?.status === 'done' && cachedEntry.rows) {
      return cachedEntry.rows;
    }

    const { data } = await api.get(buildReportPath(item.url, isInvoice));
    const rows: ReportRow[] = data?.rows || [];
    const saldoCents = getLastSaqueSaldoCents(rows);

    setSaldos(prev => ({
      ...prev,
      [item.url]: {
        status: 'done',
        value: saldoCents / 100,
        rows,
      },
    }));

    return rows;
  };

  const refreshItem = async (
    item: ReportItem,
    isInvoice: boolean,
  ): Promise<ReportRow[] | null> => {
    try {
      const { data } = await api.get(buildReportPath(item.url, isInvoice));
      const rows: ReportRow[] = data?.rows || [];
      const saldoCents = getLastSaqueSaldoCents(rows);

      setSaldos(prev => ({
        ...prev,
        [item.url]: {
          status: 'done',
          value: saldoCents / 100,
          rows,
        },
      }));

      return rows;
    } catch (err) {
      console.error('Erro ao atualizar item', err);
      return null;
    }
  };

  /* --------------------------------------------------------------- */
  /* Handlers                                                         */
  /* --------------------------------------------------------------- */

  const handleGeneratePDF = async (
    item: ReportItem,
    isInvoice: boolean,
  ) => {
    if (pdfGenerating[item.url]) return;
    setPdfGenerating(prev => ({ ...prev, [item.url]: true }));

    try {
      const rows = await ensureRows(item, isInvoice);
      if (!rows.length) {
        console.warn('Sem dados para gerar PDF', item.url);
        return;
      }

      const firstRowDate = rows[0]?.date;
      const fallbackCreatedAt =
        (firstRowDate && new Date(firstRowDate as string)) ||
        (item.last && new Date(item.last)) ||
        new Date();

      const project = {
        reference_number: item.code,
        title: item.event,
        createdAt: fallbackCreatedAt,
        url: item.url,
      };

      await handleFinanceReportPDF({
        data: { rows },
        project,
      });
    } catch (err) {
      console.error('Erro ao gerar PDF', err);
    } finally {
      setPdfGenerating(prev => ({ ...prev, [item.url]: false }));
    }
  };

  const handleAnalysis = (item: ReportItem, invoiceTab) => {
    window.open(invoiceTab ?   `/manager/check_finance_invoice/${item.url}` : `/manager/check_finance/${item.url}`,"_BLANK");
  };

  const handleSuggestions = async (
    item: ReportItem,
    isInvoice: boolean,
  ) => {
    if (suggestionsLoading[item.url]) return;
    setSuggestionsLoading(prev => ({ ...prev, [item.url]: true }));

    try {
      const rows = await ensureRows(item, isInvoice);
      // Nota: rows vazias são válidas no caso órfão — o modal decide.
      // O early return antigo bloqueava o fluxo de investigação.

      openAnalysis({
        item: {
          code: item.code,
          event: item.event,
          url: item.url,
          payment_id: item.payment_id,
          last: item.last,
          id: item.id,
          saque: item.saque,
        },
        isInvoice,
        rows,
        onRefresh: () => refreshItem(item, isInvoice),
      });
    } catch (err) {
      console.error('Erro ao abrir análise', err);
    } finally {
      setSuggestionsLoading(prev => ({ ...prev, [item.url]: false }));
    }
  };

  /* --------------------------------------------------------------- */
  /* Renderização                                                     */
  /* --------------------------------------------------------------- */

  const renderSaldoCell = (item: ReportItem, isInvoiceTab: boolean) => {
    const entry = saldos[item.url];

    if (!entry || entry.status === 'loading') {
      return <Spinner size="sm" />;
    }
    if (entry.status === 'error') {
      return <span style={{ color: textTertiary }}>—</span>;
    }

    if (isOrphanSaque(item, entry)) {
      return (
        <OrphanBadge
          type="button"
          disabled={!!suggestionsLoading[item.url]}
          title="Saque não encontrado neste relatório. Clique para investigar."
          onClick={() => handleSuggestions(item, isInvoiceTab)}
        >
          <FiAlertTriangle size={14} />
          Saque não encontrado
        </OrphanBadge>
      );
    }

    const v = entry.value ?? 0;
    const kind = v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral';

    return <SaldoText $kind={kind}>{formatCurrency(v)}</SaldoText>;
  };

  const isInvoiceTab = tab === 'mensalidades';
  const activeList = sortByLastDesc(isInvoiceTab ? invoices : events);

  return (
    <Wrapper>
      <Tabs
        variant="brand"
        items={[
          { label: `Eventos (${events.length})`, value: 'eventos' },
          {
            label: `Mensalidades (${invoices.length})`,
            value: 'mensalidades',
          },
        ]}
        active={tab}
        onChange={v => setTab(v as TabKey)}
      />

      {loading && (
        <CenterBox>
          <Spinner size="lg" />
        </CenterBox>
      )}

      {!loading && error && (
        <EmptyState icon="!" title="Erro" description={error} />
      )}

      {!loading && !error && activeList.length === 0 && (
        <EmptyState
          title="Nenhum saque nas últimas 3 semanas"
          description="Nenhum projeto teve saque nesse período."
        />
      )}

      {!loading && !error && activeList.length > 0 && (
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>Referência</Th>
                <Th>Projeto</Th>
                <Th>Valor Sacado</Th>
                <Th>Data do Saque</Th>
                <Th>Saldo da Semana</Th>
                <Th>Módulos</Th>
              </Tr>
            </Thead>
            <tbody>
              {activeList.map(item => (
                <Tr key={item.url}>
                  <Td>{item.code || '—'}</Td>
                  <Td>{item.event}</Td>
                  <Td>{formatCurrency(item.saque || 0)}</Td>
                  <Td>{item.date || '—'}</Td>
                  <Td>{renderSaldoCell(item, isInvoiceTab)}</Td>
                  <Td>
                    <Modules>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        icon={<FiFileText size={14} />}
                        loading={!!pdfGenerating[item.url]}
                        disabled={!!pdfGenerating[item.url]}
                        onClick={() =>
                          handleGeneratePDF(item, isInvoiceTab)
                        }
                      >
                        PDF
                      </Button>
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        icon={<FaChartArea size={14} />}
                        onClick={() => handleAnalysis(item, isInvoiceTab)}
                      >
                        Análise
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon={<FaLightbulb size={14} />}
                        loading={!!suggestionsLoading[item.url]}
                        disabled={!!suggestionsLoading[item.url]}
                        onClick={() =>
                          handleSuggestions(item, isInvoiceTab)
                        }
                      >
                        Sugestões
                      </Button>
                    </Modules>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </Wrapper>
  );
};

/* ------------------------------------------------------------------ */
/* Hook abridor                                                        */
/* ------------------------------------------------------------------ */

export const useOpenWithdrawReportModal = () => {
  const { addModalV2 } = useModal();

  const openTransference = () => {
    const key = 'withdraw-report-modal';
    addModalV2({
      key,
      title: 'Relatório de Saques — últimas 3 semanas',
   
      content: <WithdrawReportContent modalKey={key} />,
    });
  };

  return { openTransference };
};

export default WithdrawReportContent;
